import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserCheck, ShieldCheck, XCircle, Clock } from 'lucide-react';
import { useApi } from '@/contexts/AuthContext';
import { BrokerReturnPanel } from '@/components/submission/BrokerReturnPanel';

const flat = (v) => (Array.isArray(v) ? v : v ? [v] : []).map((x) => (typeof x === 'string' ? x : x?.reason || x?.desc || x?.message || JSON.stringify(x)));

// After scoring: say plainly what happened to the quote and what the broker does next.
// Three endings (R-20261025-01): bound, back to the broker (answers, or their call to refer), declined.
export const NextStepCard = ({ result, isStaff, onUpdated }) => {
  const navigate = useNavigate();
  const api = useApi();
  const sr = result.scoring_result || {};
  const refers = (sr.override_rules_triggered || []).filter((r) => r.action === 'AUTO_REFER' || r.action === 'AUTO_DECLINE').map((r) => r.desc || r.rule);
  const holds = [...flat(result.bind_hold), ...flat(result.bind_block), ...flat(result.capacity_block), ...flat(result.abr_block), ...flat(result.bind_error)];
  const why = [...new Set([...refers, ...holds])].slice(0, 4);
  const open = () => navigate(`/submissions/${result.submission_id}`);

  if (result.policy_id) {
    return (
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#1D9E75' }} data-testid="next-step-bound">
        <div className="flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#1D9E75' }} />
          <div className="text-xs" style={{ color: '#0D1B2A' }}>
            <p className="font-semibold">Done — the policy is bound and the documents are issued.</p>
            <p className="mt-0.5" style={{ color: '#5C6B7A' }}>Schedule, certificate of currency and closing note are on the policy. Nothing further is needed from you.</p>
            <Button size="sm" variant="outline" className="rounded-sm mt-2 text-xs" data-testid="next-step-open-policy" onClick={() => navigate('/policies')}>Open My Policies</Button>
          </div>
        </div>
      </Card>
    );
  }
  if (result.status === 'info_requested' && result.broker_action) {
    return <div data-testid="next-step-returned"><BrokerReturnPanel sub={result} api={api} onUpdated={onUpdated} isStaff={isStaff} /></div>;
  }
  if (result.status === 'referred' && result.referred_by_broker) {
    return (
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#E8A020' }} data-testid="next-step-broker-referred">
        <div className="flex items-start gap-2.5">
          <UserCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />
          <div className="text-xs" style={{ color: '#0D1B2A' }}>
            <p className="font-semibold">Referred — an underwriter will pick it up from the queue.</p>
            <p className="mt-0.5" style={{ color: '#5C6B7A' }}>You will see the outcome under My Submissions and in your notifications.</p>
            <Button size="sm" className="rounded-sm mt-2 text-xs text-white" style={{ background: '#1A4C7C' }} data-testid="next-step-open-quote" onClick={open}>Track this quote</Button>
          </div>
        </div>
      </Card>
    );
  }
  if (sr.decision === 'DECLINE' || result.status === 'declined') {
    return (
      <div className="space-y-3">
        <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#E24B4A' }} data-testid="next-step-declined">
          <div className="flex items-start gap-2.5">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E24B4A' }} />
            <div className="text-xs" style={{ color: '#0D1B2A' }}>
              <p className="font-semibold">Declined — this risk is outside what the platform will write.</p>
              {why.length > 0 && <ul className="mt-1 list-disc pl-4" style={{ color: '#5C6B7A' }}>{why.map((w, i) => <li key={i}>{w}</li>)}</ul>}
              <p className="mt-1" style={{ color: '#5C6B7A' }}>If the facts are different from what was entered, start a new submission with the corrected details.</p>
            </div>
          </div>
        </Card>
        <BrokerReturnPanel sub={result} api={api} onUpdated={onUpdated} isStaff={isStaff} />
      </div>
    );
  }
  const held = sr.decision !== 'REFER';
  return (
    <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#E8A020' }} data-testid="next-step-referred">
      <div className="flex items-start gap-2.5">
        {held ? <Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} /> : <UserCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />}
        <div className="text-xs" style={{ color: '#0D1B2A' }}>
          <p className="font-semibold">{held ? 'Priced, but held for an underwriter before it can bind.' : 'Referred to an underwriter — it is in their queue now.'}</p>
          {why.length > 0 && <ul className="mt-1 list-disc pl-4" style={{ color: '#5C6B7A' }}>{why.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          <p className="mt-1" style={{ color: '#5C6B7A' }}>
            {isStaff
              ? 'Open the quote to approve and bind, approve with conditions, request information, or decline.'
              : 'The underwriter will approve and bind it, approve it with conditions, ask you for more information, or decline it. You will see the outcome under My Submissions and in your notifications — there is nothing more to do here.'}
          </p>
          <Button size="sm" className="rounded-sm mt-2 text-xs text-white" style={{ background: '#1A4C7C' }} data-testid="next-step-open-quote" onClick={open}>
            {isStaff ? 'Open in underwriting' : 'Track this quote'}
          </Button>
        </div>
      </div>
    </Card>
  );
};
